import * as E from 'fp-ts/lib/Either'
import { pipe } from 'fp-ts/lib/pipeable'
import { sequenceT } from 'fp-ts/lib/Apply'

import {
  AP,
  dateWithinRange,
  nonNullable,
  required,
  Validator,
  ValidationError
} from '@espoon-voltti/ts-libs/lib/validation'

import { LocalDate, today } from '@espoon-voltti/ts-libs/lib/time'

import { Event } from '@espoon-voltti/nuora-domain/grants'

export const eventV =
  (path: ReadonlyArray<string>): Validator<Event, Event> =>
  (x) =>
    pipe(
      sequenceT(AP)(
        required([...path, 'name'])(x.name),
        startDateV([...path, 'startDate'], x.endDate)(x.startDate),
        endDateV([...path, 'endDate'], x.startDate)(x.endDate)
      ),
      E.map((_) => x)
    )

const startDateV =
  (
    path: ReadonlyArray<string>,
    maybeEndDate: LocalDate | null
  ): Validator<LocalDate | null, LocalDate | null> =>
  (maybeStartDate) =>
    pipe(
      nonNullable<LocalDate>(path)(maybeStartDate),
      E.fold(
        (l) =>
          pipe(
            nonNullable<LocalDate>(path)(maybeEndDate),
            E.fold(
              (_) => E.right(maybeStartDate), // start date is optional if end date is not given
              (_) =>
                E.left([
                  new ValidationError(
                    path,
                    'event startdate missing, end date given',
                    { type: 'notNil' }
                  )
                ])
            )
          ),
        dateWithinRange(path, startOfYear(), endOfYear())
      )
    )

const endDateV =
  (
    path: ReadonlyArray<string>,
    maybeStartDate: LocalDate | null
  ): Validator<LocalDate | null, LocalDate | null> =>
  (maybeEndDate) =>
    pipe(
      nonNullable<LocalDate>(path)(maybeEndDate),
      E.fold(
        (_) => E.right(maybeEndDate), // end date is optional
        (endDate) =>
          pipe(
            nonNullable<LocalDate>(path)(maybeStartDate),
            E.fold(
              (_) => dateWithinRange(path, startOfYear(), endOfYear())(endDate),
              (startDate) =>
                dateWithinRange(path, startDate, endOfYear())(endDate)
            )
          )
      )
    )

const startOfYear = (): LocalDate =>
  new LocalDate(today().d.withMonth(1).withDayOfMonth(1))

const endOfYear = (): LocalDate =>
  new LocalDate(today().d.withMonth(12).withDayOfMonth(31))
